class MegaMenuCollectionHover {
  constructor() {
    this.init();
    this.cache = new Map();
    this.currentRequest = null;
    this.hoverTimeout = null;
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupEventListeners();
    });
  }

  setupEventListeners() {
    const megaMenus = document.querySelectorAll('[data-main-products]');
    
    megaMenus.forEach(megaMenu => {
      const showEachCategory = megaMenu.dataset.showEachCategory === 'true';
      if (!showEachCategory) return;

      const parentContainer = megaMenu.closest('.sub-menu.mega-menu-container');
      if (!parentContainer) return;

      const collectionLinks = parentContainer.querySelectorAll('[data-collection-handle]');
      
      collectionLinks.forEach(link => {
        link.addEventListener('mouseenter', (e) => {
          // Debounce hover events
          if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
          }
          
          this.hoverTimeout = setTimeout(() => {
            this.handleLinkHover(e, megaMenu);
          }, 150);
        });
        
        link.addEventListener('mouseleave', () => {
          if (this.hoverTimeout) {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = null;
          }
        });
      });

      // Ana collection'a geri dön
      parentContainer.addEventListener('mouseleave', () => {
        this.resetToDefaultCollection(megaMenu);
      });
    });
  }

  async handleLinkHover(event, productsContainer) {
    const link = event.target;
    const collectionHandle = link.dataset.collectionHandle;
    const collectionTitle = link.dataset.collectionTitle;
    
    if (!collectionHandle) return;

    // Loading state göster
    this.showLoadingState(productsContainer);

    try {
      const products = await this.fetchCollectionProducts(collectionHandle);
      
      // Eğer products boş ise eski haline geri çevir
      if (!products || products.length === 0) {
        this.resetToDefaultCollection(productsContainer);
        return;
      }
      
      this.renderProducts(productsContainer, products, collectionTitle);
    } catch (error) {
      console.error('Error fetching collection products:', error);
      this.resetToDefaultCollection(productsContainer);
    }
  }

  async fetchCollectionProducts(collectionHandle, limit = 8) {
    // Cache kontrolü
    const cacheKey = `${collectionHandle}-${limit}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Önceki request'i cancel et
    if (this.currentRequest) {
      this.currentRequest.abort();
    }

    const controller = new AbortController();
    this.currentRequest = controller;

    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=${limit}`, {
        signal: controller.signal
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const products = data.products || [];

      // Cache'e ekle
      this.cache.set(cacheKey, products);
      
      return products;
    } finally {
      if (this.currentRequest === controller) {
        this.currentRequest = null;
      }
    }
  }

  renderProducts(container, products, collectionTitle) {
    // Image'ı olan ürünleri filtrele
    const productsWithImages = products.filter(product => {
      const featuredImage = product.images && product.images[0];
      return featuredImage && featuredImage.src;
    });

    const html = productsWithImages.map((product, index) => {
      // Responsive visibility classes
      let visibilityClass = '';
      if (index >= 6) {
        visibilityClass = 'hp:hidden hp:2xl:flex';
      } else if (index >= 4) {
        visibilityClass = 'hp:hidden hp:xl:flex';
      }

      const featuredImage = product.images[0];
      const imageUrl = this.getImageUrl(featuredImage, '150x0');
      const price = this.formatPrice(product.variants[0]?.price);

      return `
        <div class="hp:flex hp:flex-col hp:gap-1 hp:group hp:w-full ${visibilityClass}">
          <a href="/products/${product.handle}" class="hp:w-fit hp:block hp:flex-shrink-0">
            <img src="${imageUrl}" alt="${product.title}" loading="lazy" width="150" height="150">
          </a>
          <div>
            <a href="/products/${product.handle}" class="hp:text-sm hp:font-semibold hp:group-hover:!underline hp:!truncate hp:!max-w-full hp:block">
              ${product.title}
            </a>
            <div class="hp:text-sm hp:mt-1">
              ${price}
            </div>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = html;
    container.classList.remove('loading');
  }

  showLoadingState(container) {
    container.classList.add('loading');
    container.innerHTML = `
      <div class="hp:flex hp:items-center hp:justify-center hp:w-full hp:h-32">
        <div class="loading-overlay">
          <svg class="spinner" viewBox="0 0 50 50">
            <circle class="spinner-path" cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/>
          </svg>
        </div>
      </div>
    `;
  }

  resetToDefaultCollection(container) {
    const defaultCollection = container.dataset.defaultCollection;
    if (!defaultCollection) return;

    // Ana collection'ın ürünlerini tekrar yükle
    this.fetchCollectionProducts(defaultCollection)
      .then(products => {
        if (products && products.length > 0) {
          this.renderProducts(container, products, '');
        } else {
          // Eğer default collection da boşsa, orijinal HTML'i koru
          container.classList.remove('loading');
        }
      })
      .catch(error => {
        console.error('Error resetting to default collection:', error);
        container.classList.remove('loading');
      });
  }

  getImageUrl(image, size) {
    if (!image) return '';
    
    // Shopify image URL format
    let url = image.src;
    if (url.includes('//')) {
      url = url.replace(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?.*)?$/i, '_' + size + '.$1$2');
    } else {
      // Handle relative URLs
      url = url.replace(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?.*)?$/i, '_' + size + '.$1$2');
    }
    return url;
  }

  formatPrice(price) {
    if (!price) return '';
    
    // Parse edilmiş fiyat
    const numPrice = parseFloat(price);
    
    // Kuruş kontrolü - eğer tam sayıysa kuruş gösterme
    if (numPrice % 1 === 0) {
      return numPrice.toFixed(2) + ' TL';
    } else {
      // Kuruş varsa göster, virgül ile ayır
      return numPrice.toFixed(2).replace('.', ',') + ' TL';
    }
  }
}

// Initialize
new MegaMenuCollectionHover(); 